/**
 * Toolbar for the company list page with search, sort, and filter controls.
 *
 * Covers: AC-001 (search input), AC-002 (sort dropdown), AC-003 (filter chips),
 *         AC-004 (empty state), AC-006 (auto-focus, Escape to clear)
 */

import { useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  CompanySortField,
  SortDirection,
  CompanyTypeFilter,
  KBStatusFilter,
} from '@/hooks/useCompanyListFilters';

interface CompanyListToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: CompanySortField;
  onSortByChange: (field: CompanySortField) => void;
  sortOrder: SortDirection;
  onSortOrderChange: (order: SortDirection) => void;
  filterType: CompanyTypeFilter;
  onFilterTypeChange: (type: CompanyTypeFilter) => void;
  filterStatus: KBStatusFilter;
  onFilterStatusChange: (status: KBStatusFilter) => void;
  resultCount: number;
  totalCount: number;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const sortOptions: { value: CompanySortField; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'kb_completeness', label: 'KB Status' },
  { value: 'company_type', label: 'Type' },
  { value: 'updated_at', label: 'Last Updated' },
];

interface FilterChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function FilterChip({ label, isActive, onClick }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        isActive
          ? 'bg-primary-100 text-primary-700 border border-primary-300'
          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

export function CompanyListToolbar({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  filterType,
  onFilterTypeChange,
  filterStatus,
  onFilterStatusChange,
  resultCount,
  totalCount,
  onClearFilters,
  hasActiveFilters,
}: CompanyListToolbarProps) {
  const searchRef = useRef<HTMLInputElement>(null);

  // AC-006: Auto-focus search input on mount
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // AC-006: Clear search on Escape
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onSearchChange('');
      searchRef.current?.blur();
    }
  };

  const handleSortChange = (value: string) => {
    // Value format: "field-order" e.g. "name-asc"
    const field = value as CompanySortField;
    onSortByChange(field);
    // Toggle sort order when clicking same field, default to asc for new field
    if (field === sortBy) {
      onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortOrderChange(field === 'kb_completeness' || field === 'updated_at' ? 'desc' : 'asc');
    }
  };

  return (
    <div className="mb-6 space-y-3">
      {/* Search + Sort row */}
      <div className="flex items-center gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            ref={searchRef}
            type="text"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-9 pr-8"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sort dropdown */}
        <Select value={sortBy} onValueChange={handleSortChange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
                {sortBy === option.value && (
                  <span className="ml-1 text-gray-400">
                    {sortOrder === 'asc' ? '\u2191' : '\u2193'}
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Filter chips row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 mr-1">Type:</span>
        <FilterChip
          label="Public"
          isActive={filterType === 'public'}
          onClick={() => onFilterTypeChange(filterType === 'public' ? 'all' : 'public')}
        />
        <FilterChip
          label="Private"
          isActive={filterType === 'private'}
          onClick={() => onFilterTypeChange(filterType === 'private' ? 'all' : 'private')}
        />

        <span className="text-gray-300 mx-1">|</span>

        <span className="text-xs text-gray-500 mr-1">KB:</span>
        <FilterChip
          label="Complete"
          isActive={filterStatus === 'complete'}
          onClick={() => onFilterStatusChange(filterStatus === 'complete' ? 'all' : 'complete')}
        />
        <FilterChip
          label="Building"
          isActive={filterStatus === 'building'}
          onClick={() => onFilterStatusChange(filterStatus === 'building' ? 'all' : 'building')}
        />
        <FilterChip
          label="Stale"
          isActive={filterStatus === 'stale'}
          onClick={() => onFilterStatusChange(filterStatus === 'stale' ? 'all' : 'stale')}
        />

        {/* Result count + clear filters */}
        <div className="ml-auto flex items-center gap-2">
          {hasActiveFilters && (
            <span className="text-xs text-gray-500">
              Showing {resultCount} of {totalCount} companies
            </span>
          )}
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanyListToolbar;
